import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  FileText,
  RefreshCw,
  TrendingUp,
  ShoppingCart,
  ArrowDownToLine,
  IndianRupee,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Loader2
} from 'lucide-react';

export const ClientDetailsPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [orders, setOrders] = useState([]);
  const [sips, setSips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchClientData();
    }
  }, [clientId]);

  const fetchClientData = async () => {
    setLoading(true);
    try {
      // Fetch client details
      const clientRes = await axios.get(`${API}/bse/clients/${clientId}`);
      setClient(clientRes.data);
      
      // Fetch related data if client has UCC
      if (clientRes.data.ucc_code) {
        const [portfolioRes, ordersRes, sipsRes] = await Promise.all([
          axios.get(`${API}/bse/portfolio/${clientRes.data.ucc_code}`).catch(() => ({ data: { holdings: [] } })),
          axios.get(`${API}/bse/orders?ucc_code=${clientRes.data.ucc_code}`).catch(() => ({ data: { orders: [] } })),
          axios.get(`${API}/bse/sip?ucc_code=${clientRes.data.ucc_code}`).catch(() => ({ data: { sips: [] } }))
        ]);
        
        setPortfolio(portfolioRes.data);
        setOrders(ordersRes.data.orders || []);
        setSips(sipsRes.data.sips || []);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast.error('Failed to fetch client details');
    } finally {
      setLoading(false);
    }
  };

  const retryRegistration = async () => {
    setRetrying(true);
    try {
      const response = await axios.post(`${API}/bse/clients/${clientId}/retry-registration`);
      toast.success(`Registration successful! UCC: ${response.data.ucc_code}`);
      fetchClientData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      registered: { className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 },
      pending: { className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
      failed: { className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
      verified: { className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const getOrderStatusBadge = (status) => {
    const statusConfig = {
      initiated: { className: 'bg-gray-500/20 text-gray-400' },
      payment_pending: { className: 'bg-yellow-500/20 text-yellow-400' },
      submitted: { className: 'bg-blue-500/20 text-blue-400' },
      accepted: { className: 'bg-blue-500/20 text-blue-400' },
      confirmed: { className: 'bg-green-500/20 text-green-400' },
      processed: { className: 'bg-green-500/20 text-green-400' },
      allotted: { className: 'bg-green-500/20 text-green-400' },
      failed: { className: 'bg-red-500/20 text-red-400' },
      rejected: { className: 'bg-red-500/20 text-red-400' },
      cancelled: { className: 'bg-gray-500/20 text-gray-400' }
    };
    const config = statusConfig[status] || statusConfig.initiated;
    return <Badge className={config.className}>{status?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Client not found</p>
          <Button className="mt-4" onClick={() => navigate('/distributor')}>
            Back to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const totalPortfolioValue = portfolio?.summary?.total_value || 0;
  const totalInvested = portfolio?.summary?.total_invested || 0;
  const totalGain = portfolio?.summary?.total_gain || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-details-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/distributor')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {client.first_name} {client.middle_name} {client.last_name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-muted-foreground">{client.pan}</span>
                {getStatusBadge(client.bse_registration_status)}
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            {client.bse_registration_status === 'pending' && (
              <Button onClick={retryRegistration} disabled={retrying}>
                {retrying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Retry Registration
              </Button>
            )}
            <Button variant="outline" onClick={fetchClientData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Portfolio Value</p>
                  <p className="text-lg font-bold">₹{totalPortfolioValue.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${totalGain >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <TrendingUp className={`w-5 h-5 ${totalGain >= 0 ? 'text-green-400' : 'text-red-400'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Gain/Loss</p>
                  <p className={`text-lg font-bold ${totalGain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {totalGain >= 0 ? '+' : ''}₹{totalGain.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Orders</p>
                  <p className="text-lg font-bold">{orders.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Active SIPs</p>
                  <p className="text-lg font-bold">{sips.filter(s => s.is_active).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList className="bg-card/50 border border-border/50">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio ({portfolio?.holdings?.length || 0})</TabsTrigger>
            <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="sips">SIPs ({sips.length})</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Personal Details */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Personal Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Client Code</p>
                      <p className="font-medium">{client.client_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">UCC Code</p>
                      <p className="font-medium">{client.ucc_code || 'Not assigned'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">PAN</p>
                      <p className="font-medium font-mono">{client.pan}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Date of Birth</p>
                      <p className="font-medium">{client.date_of_birth}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">KYC Status</p>
                    <div className="mt-1">{getStatusBadge(client.kyc_status)}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Details */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{client.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{client.mobile}</span>
                  </div>
                  {client.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-1" />
                      <div>
                        <p>{client.address.address_line1}</p>
                        {client.address.address_line2 && <p>{client.address.address_line2}</p>}
                        <p>{client.address.city}, {client.address.state} - {client.address.pincode}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bank Details */}
              <Card className="bg-card/50 backdrop-blur border-border/50 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Bank Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {client.bank_accounts?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {client.bank_accounts.map((bank, index) => (
                        <div key={index} className="p-4 bg-background/50 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{bank.bank_name}</p>
                            {bank.is_default && <Badge>Primary</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">A/C: {bank.account_number}</p>
                          <p className="text-sm text-muted-foreground">IFSC: {bank.ifsc_code}</p>
                          <p className="text-sm text-muted-foreground">Type: {bank.account_type}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No bank accounts found</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <Card className="bg-card/50 backdrop-blur border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Portfolio Holdings</CardTitle>
                <CardDescription>Current mutual fund holdings</CardDescription>
              </CardHeader>
              <CardContent>
                {!portfolio?.holdings?.length ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No holdings found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b border-border/50">
                        <tr className="text-left text-sm text-muted-foreground">
                          <th className="p-4 font-medium">Scheme</th>
                          <th className="p-4 font-medium">Folio</th>
                          <th className="p-4 font-medium text-right">Units</th>
                          <th className="p-4 font-medium text-right">NAV</th>
                          <th className="p-4 font-medium text-right">Value</th>
                          <th className="p-4 font-medium text-right">Gain/Loss</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolio.holdings.map((holding, index) => (
                          <tr key={index} className="border-b border-border/30">
                            <td className="p-4">
                              <p className="font-medium">{holding.scheme_name}</p>
                              <p className="text-xs text-muted-foreground">{holding.scheme_code}</p>
                            </td>
                            <td className="p-4 text-sm">{holding.folio_number}</td>
                            <td className="p-4 text-right">{holding.units?.toFixed(3)}</td>
                            <td className="p-4 text-right">₹{holding.nav?.toFixed(2)}</td>
                            <td className="p-4 text-right font-medium">₹{holding.market_value?.toLocaleString()}</td>
                            <td className={`p-4 text-right font-medium ${holding.unrealized_gain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {holding.unrealized_gain >= 0 ? '+' : ''}₹{holding.unrealized_gain?.toLocaleString()}
                              <span className="text-xs ml-1">({holding.unrealized_gain_percentage?.toFixed(2)}%)</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="bg-card/50 backdrop-blur border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Transaction History</CardTitle>
                <CardDescription>All purchase and redemption orders</CardDescription>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No orders found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b border-border/50">
                        <tr className="text-left text-sm text-muted-foreground">
                          <th className="p-4 font-medium">Order ID</th>
                          <th className="p-4 font-medium">Type</th>
                          <th className="p-4 font-medium">Scheme</th>
                          <th className="p-4 font-medium text-right">Amount/Units</th>
                          <th className="p-4 font-medium">Status</th>
                          <th className="p-4 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(order => (
                          <tr key={order.order_id} className="border-b border-border/30">
                            <td className="p-4 font-mono text-sm">{order.order_id}</td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                {order.transaction_type === 'P' ? (
                                  <ShoppingCart className="w-4 h-4 text-green-400" />
                                ) : (
                                  <ArrowDownToLine className="w-4 h-4 text-blue-400" />
                                )}
                                <span>{order.transaction_type === 'P' ? 'Purchase' : 'Redemption'}</span>
                              </div>
                            </td>
                            <td className="p-4 text-sm">{order.scheme_code}</td>
                            <td className="p-4 text-right">
                              {order.amount ? `₹${order.amount.toLocaleString()}` : `${order.units} units`}
                            </td>
                            <td className="p-4">{getOrderStatusBadge(order.status)}</td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SIPs Tab */}
          <TabsContent value="sips">
            <Card className="bg-card/50 backdrop-blur border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Systematic Investment Plans</CardTitle>
                <CardDescription>Active and past SIP registrations</CardDescription>
              </CardHeader>
              <CardContent>
                {sips.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No SIPs found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b border-border/50">
                        <tr className="text-left text-sm text-muted-foreground">
                          <th className="p-4 font-medium">SIP ID</th>
                          <th className="p-4 font-medium">Scheme</th>
                          <th className="p-4 font-medium text-right">Amount</th>
                          <th className="p-4 font-medium">Frequency</th>
                          <th className="p-4 font-medium">Start Date</th>
                          <th className="p-4 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sips.map(sip => (
                          <tr key={sip.sip_id} className="border-b border-border/30">
                            <td className="p-4 font-mono text-sm">{sip.sip_id}</td>
                            <td className="p-4 text-sm">{sip.scheme_code}</td>
                            <td className="p-4 text-right font-medium">₹{sip.amount?.toLocaleString()}</td>
                            <td className="p-4 text-sm">{sip.frequency}</td>
                            <td className="p-4 text-sm">{sip.start_date}</td>
                            <td className="p-4">
                              <Badge className={sip.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                                {sip.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ClientDetailsPage;
